import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Alert,
  ButtonBase,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Lock as LockIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { parseLocalDate } from '../utils/dateFormatter';

/**
 * A plan's billing periods laid out as a calendar: one box per month (or ISO week), showing the
 * bucket sitting in it and whether that period is still editable.
 *
 * Two signals per box, deliberately kept separate rather than collapsed into one status:
 *   - content — empty / draft / confirmed
 *   - money   — how many subscribers have been billed for it
 * They are orthogonal. "Confirmed but not yet billed" is the state an admin most needs to see,
 * and a single enum would lose it.
 *
 * The countdown is the third. A period with nothing in it is only a problem relative to its
 * cut-off, and nothing in the old view said how long was left.
 *
 * The periods themselves come from the server. Which weeks exist in a year is ISO-8601
 * arithmetic — 2026 has 53 — and that belongs in one tested place, not in two React apps.
 */
const PlanPeriodCalendar = ({ plan, loadCalendar, onAddForPeriod, onOpenIteration, refreshKey }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isWeekly = plan?.type === 'WEEKLY';

  const fetchYear = useCallback(async (targetYear) => {
    if (!plan?.id) return;
    setLoading(true);
    setError(null);
    try {
      // Ask for the whole year; the server decides how many periods that actually is.
      const from = isWeekly ? `${targetYear}-W01` : `${targetYear}-01`;
      const to = isWeekly ? `${targetYear}-W53` : `${targetYear}-12`;
      setCalendar(await loadCalendar(plan.id, from, to));
    } catch (err) {
      console.error('Error loading plan calendar:', err);
      setError(err.response?.data?.message || 'Failed to load the calendar');
    } finally {
      setLoading(false);
    }
  }, [plan, isWeekly, loadCalendar]);

  useEffect(() => { fetchYear(year); }, [fetchYear, year, refreshKey]);

  const label = (period) => {
    if (!isWeekly) {
      return parseLocalDate(period.periodStart).toLocaleDateString(undefined, { month: 'short' });
    }
    return `Week ${Number(period.periodKey.split('-W')[1])}`;
  };

  const dateRange = (period) => {
    const start = parseLocalDate(period.periodStart);
    const end = parseLocalDate(period.periodEnd);
    end.setDate(end.getDate() - 1); // periodEnd is exclusive
    const fmt = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString(undefined, fmt)} – ${end.toLocaleDateString(undefined, fmt)}`;
  };

  const statusChip = (period) => {
    if (!period.iterationId) return null;
    const confirmed = period.status === 'CONFIRMED';
    return (
      <Chip size="small" label={confirmed ? 'confirmed' : 'draft'}
        color={confirmed ? 'success' : 'default'}
        variant={confirmed ? 'filled' : 'outlined'} />
    );
  };

  /**
   * "3 days left" on any period whose content is not settled yet. Once the cut-off passes the
   * lock icon says the rest, so this stops rather than counting into the negative.
   */
  const countdown = (period) => {
    if (period.cutoffPassed || period.status === 'CONFIRMED') return null;
    const days = period.daysUntilCutoff;
    if (days == null || days > 14) return null;
    return (
      <Typography variant="caption" display="block" fontWeight={700}
        color={days <= 2 ? 'error.main' : 'warning.main'}>
        {days === 0 ? 'Confirm today' : `${days} day${days === 1 ? '' : 's'} left`}
      </Typography>
    );
  };

  if (loading && !calendar) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" onClick={() => setYear((y) => y - 1)} aria-label="Previous year">
            <PrevIcon />
          </IconButton>
          <Typography variant="h6">{year}</Typography>
          <IconButton size="small" onClick={() => setYear((y) => y + 1)} aria-label="Next year">
            <NextIcon />
          </IconButton>
          {loading && <CircularProgress size={16} />}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {isWeekly ? 'One bucket per week' : 'One bucket per month'} · a locked period is past its
          billing cut-off, so changing its book no longer changes what is charged
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={1.5}>
        {(calendar?.periods || []).map((period) => {
          const empty = !period.iterationId;
          return (
            <Grid key={period.periodKey}
              size={isWeekly ? { xs: 4, sm: 3, md: 2 } : { xs: 6, sm: 4, md: 3 }}>
              <ButtonBase
                sx={{ width: '100%', textAlign: 'left', borderRadius: 1 }}
                onClick={() => (empty
                  ? onAddForPeriod(period.periodKey)
                  : onOpenIteration(period))}
              >
                <Card
                  variant="outlined"
                  sx={{
                    width: '100%',
                    minHeight: isWeekly ? 96 : 132,
                    borderStyle: empty ? 'dashed' : 'solid',
                    borderColor: !empty && period.booksFrozen ? 'error.light'
                      : !empty && period.cutoffPassed ? 'warning.light' : 'divider',
                    bgcolor: empty ? 'transparent' : 'background.paper',
                    opacity: empty ? 0.75 : 1,
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                      <Typography variant="subtitle2" fontWeight={700}>{label(period)}</Typography>
                      {!empty && (period.booksFrozen || period.cutoffPassed) && (
                        <Tooltip
                          title={period.booksFrozen
                            ? `Paid for by ${period.chargedCount} subscriber${period.chargedCount === 1 ? '' : 's'} — its book can no longer change`
                            : `Locked for billing since ${new Date(period.cutoffAt).toLocaleDateString()} — changes won't affect this period's charge`}
                          slotProps={{ transition: { timeout: 0 } }}>
                          <LockIcon fontSize="small"
                            color={period.booksFrozen ? 'error' : 'warning'} />
                        </Tooltip>
                      )}
                    </Box>

                    {isWeekly && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {dateRange(period)}
                      </Typography>
                    )}

                    {empty ? (
                      <Box mt={1}>
                        <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                          <AddIcon fontSize="small" />
                          <Typography variant="caption">Add</Typography>
                        </Box>
                        {countdown(period)}
                      </Box>
                    ) : (
                      <Box mt={0.5}>
                        <Box mb={0.5}>{statusChip(period)}</Box>
                        {countdown(period)}
                        <Typography variant="caption" display="block" noWrap
                          title={period.contentBundleTitle || ''}>
                          {period.contentBundleTitle || 'No content bundle'}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary" noWrap
                          title={period.bookBundleTitle || 'Digital only'}>
                          {period.bookBundleTitle || 'Digital only'}
                        </Typography>
                        {period.iterationCount > 1 && (
                          <Tooltip title={`${period.iterationCount} iterations this period — open to see them`}
                            slotProps={{ transition: { timeout: 0 } }}>
                            <Chip size="small" variant="outlined" sx={{ mt: 0.5, mr: 0.5 }}
                              label={`+${period.iterationCount - 1} content`} />
                          </Tooltip>
                        )}
                        {period.chargedCount > 0 && (
                          <Chip size="small" color="primary" sx={{ mt: 0.5 }}
                            label={`Billed to ${period.chargedCount}`} />
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </ButtonBase>
            </Grid>
          );
        })}
      </Grid>

      {calendar && calendar.periods?.length === 0 && (
        <Alert severity="info">No periods in this range.</Alert>
      )}
    </Box>
  );
};

export default PlanPeriodCalendar;
